"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cn } from "../utils";
import { Icon } from "../icon";
import { linkButtonVariants } from "./link-button.variants";
import type { LinkButtonProps } from "./link-button.types";

/**
 * Link Button Component
 *
 * V&A Waterfront Design System - Link Button
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=9858-7366
 *
 * Inline text link component with optional underline styling.
 * Typically renders as an `<a>` element. Use asChild with Next.js Link for routing.
 *
 * @example
 * // Basic usage
 * <LinkButton href="/page">Learn more</LinkButton>
 *
 * // With underline always visible
 * <LinkButton href="/page" underline>Always underlined</LinkButton>
 *
 * // Secondary variant
 * <LinkButton href="/page" variant="secondary">Less prominent link</LinkButton>
 *
 * // With Next.js Link
 * <LinkButton asChild variant="primary">
 *   <Link href="/page">Navigate</Link>
 * </LinkButton>
 */
const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>((t0, ref) => {
  const $ = _c(39);
  let children;
  let className;
  let disabled;
  let iconLeft;
  let iconRight;
  let onClick;
  let props;
  let size;
  let t1;
  let underline;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      underline,
      asChild: t1,
      disabled,
      iconLeft,
      iconRight,
      children,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = iconLeft;
    $[5] = iconRight;
    $[6] = onClick;
    $[7] = props;
    $[8] = size;
    $[9] = t1;
    $[10] = underline;
    $[11] = variant;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    iconLeft = $[4];
    iconRight = $[5];
    onClick = $[6];
    props = $[7];
    size = $[8];
    t1 = $[9];
    underline = $[10];
    variant = $[11];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? Slot : "a";
  let t2;
  if ($[12] !== disabled || $[13] !== onClick) {
    t2 = event => {
      if (disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      onClick?.(event);
    };
    $[12] = disabled;
    $[13] = onClick;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const handleClick = t2;
  let t3;
  if ($[15] !== className || $[16] !== size || $[17] !== underline || $[18] !== variant) {
    t3 = cn(linkButtonVariants({
      variant,
      size,
      underline
    }), className);
    $[15] = className;
    $[16] = size;
    $[17] = underline;
    $[18] = variant;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const t4 = disabled || undefined;
  const t5 = disabled || undefined;
  const t6 = disabled ? -1 : undefined;
  let t7;
  if ($[20] !== iconLeft) {
    t7 = iconLeft && <Icon name={iconLeft} />;
    $[20] = iconLeft;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== asChild || $[23] !== children) {
    t8 = asChild ? <Slottable>{children}</Slottable> : <span>{children}</span>;
    $[22] = asChild;
    $[23] = children;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== iconRight) {
    t9 = iconRight && <Icon name={iconRight} />;
    $[25] = iconRight;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== Comp || $[28] !== handleClick || $[29] !== props || $[30] !== ref || $[31] !== t3 || $[32] !== t4 || $[33] !== t5 || $[34] !== t6 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t10 = <Comp ref={ref} data-slot="link-button" className={t3} aria-disabled={t4} data-disabled={t5} tabIndex={t6} onClick={handleClick} {...props}>{t7}{t8}{t9}</Comp>;
    $[27] = Comp;
    $[28] = handleClick;
    $[29] = props;
    $[30] = ref;
    $[31] = t3;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
});
LinkButton.displayName = "LinkButton";
export { LinkButton };